import {
    Box,
    Button,
    Checkbox,
    FormControl,
    Heading,
    Text,
    useToast
} from '@chakra-ui/react'
import {
    AgentCnpjInput,
    InputBuild,
    SelectBuild
} from 'components/molecules'
import React, { useMemo, useState } from 'react'
import ServiceFee from 'services/fee'
import formatter from 'utils/formatter'

const getCurrentMonth = () => String(new Date().getMonth() + 1)
const getCurrentYear = () => String(new Date().getFullYear())
const formatDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}
const getCurrentPeriodStart = () => {
    const now = new Date()
    return formatDate(new Date(now.getFullYear(), now.getMonth(), 1))
}
const getCurrentPeriodEnd = () => {
    const now = new Date()
    return formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))
}

type UfDetran = 'MS' | 'RS' | 'PR' | 'ES' | 'TO' | 'RN'
type TipoPagamento = 'POS_PAGO' | 'PRE_PAGO'

const PaymentGuide: React.FC = () => {
    const [cnpj, setCnpj] = useState('')
    const [tipoPagamento, setTipoPagamento] =
        useState<TipoPagamento>('POS_PAGO')
    const [uf, setUf] = useState<UfDetran>('MS')
    const [mesDebito, setMesDebito] = useState(getCurrentMonth())
    const [anoDebito, setAnoDebito] = useState(getCurrentYear())
    const [periodoInicio, setPeriodoInicio] = useState(getCurrentPeriodStart())
    const [periodoFim, setPeriodoFim] = useState(getCurrentPeriodEnd())
    const [idsTaxas, setIdsTaxas] = useState('')
    const [quantidadeTaxas, setQuantidadeTaxas] = useState('')
    const [regerarDebito, setRegerarDebito] = useState(false)
    const [loading, setLoading] = useState(false)

    const toast = useToast()

    const tipoPagamentoOptions = [
        { text: 'Pós-pago', value: 'POS_PAGO' },
        { text: 'Pré-pago', value: 'PRE_PAGO' }
    ]

    const ufOptions = [
        { text: 'ES', value: 'ES' },
        { text: 'MS', value: 'MS' },
        { text: 'PR', value: 'PR' },
        { text: 'RN', value: 'RN' },
        { text: 'RS', value: 'RS' },
        { text: 'TO', value: 'TO' }
    ]

    const filteredUfOptions = useMemo(() => {
        if (tipoPagamento === 'PRE_PAGO') {
            return ufOptions.filter((option) => option.value === 'RS')
        }

        return ufOptions.filter((option) => option.value !== 'RS')
    }, [tipoPagamento])

    const mesOptions = [
        { text: 'Janeiro', value: '1' },
        { text: 'Fevereiro', value: '2' },
        { text: 'Março', value: '3' },
        { text: 'Abril', value: '4' },
        { text: 'Maio', value: '5' },
        { text: 'Junho', value: '6' },
        { text: 'Julho', value: '7' },
        { text: 'Agosto', value: '8' },
        { text: 'Setembro', value: '9' },
        { text: 'Outubro', value: '10' },
        { text: 'Novembro', value: '11' },
        { text: 'Dezembro', value: '12' }
    ]

    const isES = uf === 'ES'
    const isPR = uf === 'PR'
    const isRN = uf === 'RN'
    const isRS = uf === 'RS'
    const isTO = uf === 'TO'

    const idsTaxasFormatado = idsTaxas
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
        .join(',')

    const isCnpjValido = cnpj.replace(/\D/g, '').length === 14
    const isMesValido = Number(mesDebito) >= 1 && Number(mesDebito) <= 12
    const isAnoValido = /^\d{4}$/.test(anoDebito)
    const isPeriodoValido =
        /^\d{4}-\d{2}-\d{2}$/.test(periodoInicio) &&
        /^\d{4}-\d{2}-\d{2}$/.test(periodoFim) &&
        periodoInicio <= periodoFim
    const isQuantidadeTaxasValida =
        /^\d+$/.test(quantidadeTaxas) && Number(quantidadeTaxas) > 0

    const canSubmit =
        (isTO || isCnpjValido) &&
        (!(isES || isRN) || (isMesValido && isAnoValido)) &&
        (!isPR || isPeriodoValido) &&
        (!isRS || isQuantidadeTaxasValida)

    const gridTemplateColumns = isRS
        ? {
              base: '1fr',
              md: '180px 100px 350px 180px max-content'
          }
        : isRN
        ? {
              base: '1fr',
              md: 'repeat(3, minmax(0, 1fr))',
              xl: 'repeat(4, minmax(0, 1fr))',
              '2xl': '180px 100px minmax(240px, 1fr) 180px 150px 150px max-content'
          }
        : isES
        ? {
              base: '1fr',
              md: '180px 100px 350px 220px 160px max-content'
          }
        : isPR
        ? {
              base: '1fr',
              md: '180px 100px 350px 180px 180px max-content'
          }
        : isTO
        ? {
              base: '1fr',
              md: '180px 100px 350px max-content'
          }
        : {
              base: '1fr',
              md: '180px 100px 350px max-content'
          }

    const handleTipoPagamentoChange = (value: TipoPagamento) => {
        setTipoPagamento(value)
        setIdsTaxas('')
        setQuantidadeTaxas('')
        setRegerarDebito(false)

        if (value === 'PRE_PAGO') {
            setUf('RS')
            return
        }

        setUf('MS')
    }

    const handleUfChange = (value: UfDetran) => {
        setUf(value)
        setRegerarDebito(false)

        if (value !== 'TO') {
            setIdsTaxas('')
        }

        if (value !== 'RS') {
            setQuantidadeTaxas('')
        }

        if (value === 'ES' || value === 'RN') {
            setMesDebito(getCurrentMonth())
            setAnoDebito(getCurrentYear())
        }

        if (value === 'PR') {
            setPeriodoInicio(getCurrentPeriodStart())
            setPeriodoFim(getCurrentPeriodEnd())
        }
    }

    const handleIdsTaxasChange = (value: string) => {
        setIdsTaxas(
            value
                .replace(/[^\d,]/g, '')
                .replace(/,{2,}/g, ',')
                .replace(/^,/, '')
        )
    }

    const handleDownload = async () => {
        if (!canSubmit) return

        setLoading(true)

        try {
            const cnpjLimpo = cnpj.replace(/\D/g, '')

            if (isRN && regerarDebito) {
                const referencia = `${anoDebito}${mesDebito.padStart(2, '0')}`
                const response = await ServiceFee.regerarDebitoRN(cnpjLimpo, referencia)
                toast({
                    title: 'Sucesso!',
                    description: response.data || 'Débito regerado com sucesso.',
                    status: 'success',
                    position: 'bottom-left'
                })
                return
            }

            const response = isRN
                ? await ServiceFee.downloadRemessaRN({
                      financeiraCnpj: cnpjLimpo,
                      mesReferencia: Number(mesDebito),
                      anoReferencia: Number(anoDebito)
                  })
                : await ServiceFee.downloadGuiaPagamentoTxt(
                isTO ? undefined : cnpjLimpo,
                uf,
                isES
                    ? {
                          mesDebito: Number(mesDebito),
                          anoDebito: Number(anoDebito)
                      }
                    : isPR
                    ? {
                          periodoInicio,
                          periodoFim
                      }
                    : isTO
                    ? {
                          debitos: idsTaxasFormatado
                      }
                    : isRS
                    ? {
                          quantidadeTaxas: Number(quantidadeTaxas)
                      }
                    : undefined
                  )

            const extension = 'txt'
            const contentType =
                response.headers['content-type'] || 'text/plain;charset=UTF-8'

            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: contentType })
            )

            const link = document.createElement('a')
            const contentDisposition = response.headers['content-disposition']

            const dataAtual = new Date().toISOString().split('T')[0]
            const nomeArquivoPadrao = isTO
                ? `Remessa_${uf}_${dataAtual}.${extension}`
                : `Remessa_${uf}_${cnpjLimpo}_${dataAtual}.${extension}`

            const fileName = contentDisposition
                ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
                : nomeArquivoPadrao

            link.href = url
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            link.remove()

            window.URL.revokeObjectURL(url)

            toast({
                title: 'Sucesso!',
                description: 'Arquivo gerado.',
                status: 'success',
                position: 'bottom-left'
            })
        } catch (error: any) {
            let mensagemExibicao = 'Erro ao processar solicitação.'

            if (error.response) {
                const data = error.response.data

                if (data instanceof Blob) {
                    try {
                        const texto = await data.text()
                        const objErro = JSON.parse(texto)

                        mensagemExibicao =
                            objErro.erros?.[0]?.mensagem ||
                            objErro[0]?.mensagem ||
                            objErro.title ||
                            objErro.message ||
                            mensagemExibicao
                    } catch (e) {
                        console.error(
                            'Não foi possível converter o Blob de erro em JSON',
                            e
                        )
                    }
                } else if (data.erros && data.erros.length > 0) {
                    mensagemExibicao = data.erros[0].mensagem
                } else if (data[0]?.mensagem) {
                    mensagemExibicao = data[0].mensagem
                } else if (data.message) {
                    mensagemExibicao = data.message
                }
            }

            toast({
                title: 'Atenção',
                description: mensagemExibicao,
                status: 'error',
                duration: 7000,
                isClosable: true,
                position: 'bottom-left'
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box backgroundColor="white" p={8} borderRadius="md" boxShadow="sm">
            <Heading size="md" mb={4}>
                Geração de Remessa de Taxas
            </Heading>

            <Text mb={6} color="gray.600">
                Informe os dados abaixo para {isRN && regerarDebito
                    ? 'regerar o débito'
                    : 'gerar o arquivo de remessa'} para o Detran-{uf}.
            </Text>

            <Box
                display="grid"
                gridTemplateColumns={gridTemplateColumns}
                alignItems="flex-end"
                gap={4}
            >
                <FormControl>
                    <SelectBuild
                        id="tipo-pagamento-select"
                        label="Tipo Pagamento"
                        options={tipoPagamentoOptions}
                        value={tipoPagamento}
                        onChange={(e: any) =>
                            handleTipoPagamentoChange(
                                e.target.value as TipoPagamento
                            )
                        }
                    />
                </FormControl>

                <FormControl>
                    <SelectBuild
                        id="uf-select"
                        label="UF"
                        options={filteredUfOptions}
                        value={uf}
                        onChange={(e: any) =>
                            handleUfChange(e.target.value as UfDetran)
                        }
                    />
                </FormControl>

                {!isTO && (
                    <FormControl>
                        <AgentCnpjInput
                            id="cnpj-input"
                            label="CNPJ da Financeira"
                            uf={uf}
                            placeholder="00.000.000/0000-00"
                            value={cnpj}
                            required={true}
                            onChange={setCnpj}
                        />
                    </FormControl>
                )}

                {(isES || isRN) && (
                    <FormControl>
                        <SelectBuild
                            id="mes-debito-select"
                            label={isRN ? 'Mês Referência' : 'Mês Débito'}
                            options={mesOptions}
                            value={mesDebito}
                            required={true}
                            onChange={(e: any) =>
                                setMesDebito(e.target.value)
                            }
                        />
                    </FormControl>
                )}

                {(isES || isRN) && (
                    <FormControl>
                        <InputBuild
                            id="ano-debito-input"
                            label={isRN ? 'Ano Referência' : 'Ano Débito'}
                            placeholder="AAAA"
                            value={anoDebito}
                            required={true}
                            maxLength={4}
                            onChange={(e: any) =>
                                setAnoDebito(
                                    formatter
                                        .onlyNumber(e.target.value)
                                        .slice(0, 4)
                                )
                            }
                        />
                    </FormControl>
                )}

                {isPR && (
                    <FormControl>
                        <InputBuild
                            id="periodo-inicio-input"
                            label="Período Inicial"
                            type="date"
                            value={periodoInicio}
                            required={true}
                            onChange={(e: any) =>
                                setPeriodoInicio(e.target.value)
                            }
                        />
                    </FormControl>
                )}

                {isPR && (
                    <FormControl>
                        <InputBuild
                            id="periodo-fim-input"
                            label="Período Final"
                            type="date"
                            value={periodoFim}
                            required={true}
                            onChange={(e: any) =>
                                setPeriodoFim(e.target.value)
                            }
                        />
                    </FormControl>
                )}

                {isTO && (
                    <FormControl>
                        <InputBuild
                            id="ids-taxas-input"
                            label="IDs das Taxas"
                            placeholder="Ex.: 123,124,125"
                            value={idsTaxas}
                            onChange={(e: any) =>
                                handleIdsTaxasChange(e.target.value)
                            }
                        />
                    </FormControl>
                )}

                {isRS && (
                    <FormControl>
                        <InputBuild
                            id="quantidade-taxas-input"
                            label="Quantidade de taxas"
                            placeholder="Ex.: 50"
                            type="number"
                            min={1}
                            step={1}
                            value={quantidadeTaxas}
                            required={true}
                            onChange={(e: any) =>
                                setQuantidadeTaxas(
                                    formatter.onlyNumber(e.target.value)
                                )
                            }
                        />
                    </FormControl>
                )}

                {isRN && (
                    <Checkbox
                        isChecked={regerarDebito}
                        onChange={(e) => setRegerarDebito(e.target.checked)}
                    >
                        Regerar débito
                    </Checkbox>
                )}

                <Button
                    colorScheme="primary"
                    onClick={handleDownload}
                    isLoading={loading}
                    px={8}
                    isDisabled={!canSubmit}
                >
                    {isRN && regerarDebito ? 'Regerar débito' : 'Gerar Remessa'}
                </Button>
            </Box>
        </Box>
    )
}

export default PaymentGuide
